import { useState } from 'react';
import type { TableVisualization, TableCell } from '../../types';
import { renderMarkdownLinks, hexToTintedRgba } from '../../utils';
import { VisualizationRenderer } from './VisualizationRenderer';

interface TableRendererProps {
    data: TableVisualization;
    className?: string;
}

function CellContent({ cell }: { cell: TableCell | undefined }) {
    if (!cell) return null;

    if (cell.visualization) {
        return <VisualizationRenderer visualization={cell.visualization} />;
    }
    if (cell.markdown) {
        return (
            <span className="weavable-table__cell-markdown">
                {renderMarkdownLinks(cell.markdown, 'weavable-table__cell-link')}
            </span>
        );
    }
    if (cell.plainText) {
        const textContent = cell.plainTextUrl ? (
            <a href={cell.plainTextUrl} className="weavable-table__cell-link" target="_blank" rel="noopener noreferrer">
                {cell.plainText}
            </a>
        ) : (
            cell.plainText
        );

        if (cell.truncatePlainText) {
            return (
                <span className="weavable-table__cell-text weavable-table__cell-text--truncate" title={cell.plainText}>
                    {textContent}
                </span>
            );
        }
        return <span className="weavable-table__cell-text">{textContent}</span>;
    }
    return null;
}

/**
 * Renders a TableVisualization with columns, rows, section headers,
 * row backgrounds, rowspan support, and "Show more" expansion.
 */
export function TableRenderer({ data, className }: TableRendererProps) {
    const [expanded, setExpanded] = useState(false);

    const { initialRowLimit } = data;
    const totalRows = data.rows.length;
    const hasMoreRows = initialRowLimit !== undefined && totalRows > initialRowLimit;
    const hiddenRowCount = hasMoreRows ? totalRows - initialRowLimit : 0;
    const displayedRows = !expanded && hasMoreRows ? data.rows.slice(0, initialRowLimit) : data.rows;

    // Pre-compute rowspan skip set and clamped rowspan values for displayed rows.
    // This correctly handles: multiple rowspans per column, initialRowLimit truncation,
    // and expansion state changes.
    const skipCells = new Set<string>(); // "rowIndex:columnKey"
    const clampedRowspans = new Map<string, number>(); // "rowIndex:columnKey" -> clamped value

    for (let ri = 0; ri < displayedRows.length; ri++) {
        const row = displayedRows[ri];
        if (row.sectionHeader) continue;
        for (const column of data.columns) {
            const cell = row.cells[column.key];
            if (cell?.rowspan && cell.rowspan > 1) {
                const visibleSpan = Math.min(cell.rowspan, displayedRows.length - ri);
                if (visibleSpan !== cell.rowspan) {
                    clampedRowspans.set(`${ri}:${column.key}`, visibleSpan);
                }
                for (let s = 1; s < visibleSpan; s++) {
                    skipCells.add(`${ri + s}:${column.key}`);
                }
            }
        }
    }

    return (
        <div className={`weavable-table ${className || ''}`.trim()}>
            {data.title && <div className="weavable-table__title">{data.title}</div>}
            {data.descriptionMarkdown && (
                <div className="weavable-table__description">
                    {renderMarkdownLinks(data.descriptionMarkdown, 'weavable-table__cell-link')}
                </div>
            )}
            <div className="weavable-table__wrapper">
                <table className="weavable-table__table">
                    {!data.hideHeader && (
                        <thead>
                            <tr>
                                {data.columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className="weavable-table__header-cell"
                                        style={{
                                            width: column.width,
                                            fontWeight: data.headerBold ? 600 : 500,
                                            backgroundColor: hexToTintedRgba(data.headerBackgroundColor),
                                        }}
                                    >
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                    )}
                    <tbody>
                        {displayedRows.map((row, rowIndex) => {
                            if (row.sectionHeader) {
                                return (
                                    <tr key={row.id}>
                                        <td
                                            colSpan={data.columns.length}
                                            className="weavable-table__section-header"
                                            style={{
                                                backgroundColor: hexToTintedRgba(row.sectionHeaderColor),
                                            }}
                                        >
                                            {row.sectionHeader}
                                        </td>
                                    </tr>
                                );
                            }

                            const rowBgColor = hexToTintedRgba(row.backgroundColor);

                            return (
                                <tr key={row.id}>
                                    {data.columns.map((column) => {
                                        if (skipCells.has(`${rowIndex}:${column.key}`)) {
                                            return null;
                                        }
                                        const cell = row.cells[column.key];
                                        const effectiveRowspan =
                                            clampedRowspans.get(`${rowIndex}:${column.key}`) ?? cell?.rowspan;
                                        return (
                                            <td
                                                key={column.key}
                                                rowSpan={effectiveRowspan}
                                                className="weavable-table__cell"
                                                style={{ backgroundColor: rowBgColor }}
                                            >
                                                <CellContent cell={cell} />
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {hasMoreRows && (
                <button className="weavable-table__show-more" onClick={() => setExpanded(!expanded)}>
                    {expanded ? 'Show less' : `Show ${hiddenRowCount} more`}
                </button>
            )}
        </div>
    );
}
